import { useState } from 'react';
import { Paintbrush, Server, TrendingUp, CheckCircle, XCircle } from 'lucide-react';
import './Offerings.css';   
import { Link } from 'react-router-dom';

const Offerings = () => {
  const [hoveredCard, setHoveredCard] = useState(null);

  const offerings = [
    {
      icon: <Paintbrush size={48} />,
      title: "Creative & Branded Content",
      description: "Stop being ignored. We craft engaging, high-converting content that tells your story and attracts your ideal customer.",
      features: [ "Professional Advertising Assets", "Social Media Management", "Brand Identity Design" ],
      // Keeping these tailored classes for dynamic coloring
      gradient: "from-purple-500 to-pink-500" 
    },
    {
      icon: <Server size={48} />,
      title: "Scalable Tech & SaaS Solutions",
      description: "Don't get held back by clunky tech. We provide intuitive software platforms and scalable digital tools tailored to grow with you.",
      features: [ "Custom SaaS Development", "Workflow Automation Tools", "Tech Stack Consultation" ],
      gradient: "from-blue-500 to-cyan-500"
    },
    {
      icon: <TrendingUp size={48} />,
      title: "Web & Conversion Engines",
      description: "Your website should be your best salesperson. We build user-friendly, high-performance sites designed to turn visitors into leads.",
      features: [ "High-Converting Websites", "Landing Page Optimization", "Performance-Driven Management" ],
      gradient: "from-orange-500 to-red-500"
    }
  ];

  const comparisons = [
    {
      struggle: "Juggling 5 different freelancers for design, code, and ads.",
      advantage: "One unified team managing your entire digital ecosystem."
    },
    {
      struggle: "Generic solutions that don't fit your niche.",
      advantage: "Tailored strategies built specifically for early-stage growth."
    },
    {
      struggle: "Hidden costs and surprise fees that nobody wants.",
      advantage: "Transparent, scalable pricing designed for startup budgets."
    }
  ];

  return (
    <div className="offerings-page">
      
      {/* Section 4: Offerings */}
      <section className="section-offerings">
        <div className="container-max">
          <h3 className="headline-gradient">
            Everything You Need to Compete From Day One
          </h3>
          
          <div className="offerings-grid">
            {offerings.map((offering, index) => (
              <div
                key={index}
                className={`offering-card ${hoveredCard === index ? 'card-hovered' : ''}`}
                onMouseEnter={() => setHoveredCard(index)}
                onMouseLeave={() => setHoveredCard(null)}
              >
                {/* Gradient glow effect - uses dynamic Tailwind colors */}
                <div className={`card-glow-bg bg-gradient-to-br ${offering.gradient} ${hoveredCard === index ? 'opacity-30' : ''}`}></div>
                
                <div className="card-content">
                  {/* Icon - uses dynamic Tailwind colors */}
                  <div className={`card-icon-wrapper bg-gradient-to-br ${offering.gradient} ${hoveredCard === index ? 'icon-hovered' : ''}`}>
                    {offering.icon}
                  </div>
                  
                  <h4 className="card-title">{offering.title}</h4>
                  <p className="card-description">{offering.description}</p>
                  
                  <ul className="feature-list">
                    {offering.features.map((feature, idx) => (
                      <li key={idx} className="feature-item">
                        <CheckCircle size={20} className="feature-icon" />
                        <span className="feature-text">{feature}</span>
                      </li>
                    ))}
                  </ul>
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Section 5: Differentiator */}
      <section className="section-differentiator">
        <div className="container-narrow">
          <h3 className="section-headline">More Than Just A Vendor.</h3>
          <p className="section-subheadline">
            We aren't a distant agency billing you for every phone call, and we aren't a fragmented marketplace of freelancers. We are an integrated extension of your team.
          </p>

          <div className="comparison-list">
            {comparisons.map((comp, index) => (
              <div key={index} className="comparison-card">
                
                {/* The Struggle Side */}
                <div className="comparison-half struggle-bg">
                  <div className="comparison-content">
                    <XCircle size={28} className="icon-struggle" />
                    <div>
                      <h5 className="label-struggle">The Startup Struggle</h5>
                      <p className="text-body">{comp.struggle}</p>
                    </div>
                  </div>
                </div>

                {/* The Advantage Side */}
                <div className="comparison-half advantage-bg">
                  <div className="comparison-content">
                    <CheckCircle size={28} className="icon-advantage" />
                    <div>
                      <h5 className="label-advantage">The Trailblaze Advantage</h5>
                      <p className="text-body">{comp.advantage}</p>
                    </div>
                  </div>
                </div>

              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Section 6: Final CTA */}
      <section className="section-cta">
        {/* Animated background effects */}
        <div className="cta-overlay"></div>
        <div className="cta-blobs">
          <div className="blob blob-purple"></div>
          <div className="blob blob-pink"></div>
        </div>

        <div className="cta-content">
          <h2 className="cta-headline">
            Ready to stop surviving and start trailblazing?
          </h2>
          <p className="cta-subheadline">
            Let's discuss your vision. We'll provide a free initial assessment of what tech and creative tools you need to hit your next milestone.
          </p>

            <Link to="/contact" className='cta-button magnetic-element'>Book your Strategy Call</Link> 

          <p className="cta-disclaimer">
            No pressure, no obligation. Just clear next steps.
          </p>
        </div>
      </section>
    </div>
  );
};

export default Offerings;